import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Heading,
  Link,
  Paragraph,
  Table,
  Tag,
} from '@digdir/designsystemet-react';
import { useSafetyRepWorkQuery } from '../../hooks/queries/safetyRep/useSafetyRepWork';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { SafetyRepHero } from './components/SafetyRepHero';
import { WorkState } from './components/WorkState';
import {
  highRiskText,
  lastSurveyText,
  nextShort,
  teamPathFor,
  stageColor,
  stageText,
  teamLeaders,
} from './safetyRepWorkView';
import layout from '../HRDashboard/hmsPage.module.css';
import classes from './SafetyRep.module.css';

const COLUMNS = ['team', 'leader', 'step', 'highRisk', 'last', 'next'];

export const SafetyRepTeams = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useSafetyRepWorkQuery(
    questionLanguageFor(i18n.language),
  );

  return (
    <div className={layout.page} data-size="md">
      <SafetyRepHero
        title={t('safetyRep.work.teams.pageTitle')}
        intro={t('safetyRep.work.teams.pageIntro')}
      />
      {data ? (
        <div className={classes.column}>
          <section
            aria-labelledby="vo-all-teams"
            className={`${layout.card} ${classes.tableCard}`}
          >
            <div className={layout.cardHeading}>
              <Heading level={2} data-size="sm" id="vo-all-teams">
                {t('safetyRep.work.teams.allTitle')}
              </Heading>
              <Paragraph data-size="sm" className={layout.subtle}>
                {t('safetyRep.work.teams.anonymous')}
              </Paragraph>
            </div>
            {data.teams.length === 0 ? (
              <Paragraph>{t('safetyRep.work.teams.none')}</Paragraph>
            ) : (
              <div className={layout.tableScroll}>
                <Table data-size="sm" className={classes.table}>
                  <Table.Head>
                    <Table.Row>
                      {COLUMNS.map((key) => (
                        <Table.HeaderCell key={key}>
                          {t(`safetyRep.work.teams.table.${key}`)}
                        </Table.HeaderCell>
                      ))}
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {data.teams.map((team) => (
                      <Table.Row key={team.id}>
                        <Table.HeaderCell scope="row">
                          <span className={classes.teamName}>
                            {team.survey ? (
                              <Link asChild>
                                <RouterLink
                                  to={teamPathFor(team, team.survey.id)}
                                >
                                  {team.name}
                                </RouterLink>
                              </Link>
                            ) : (
                              team.name
                            )}
                            <span
                              className={`ds-paragraph ${layout.subtle}`}
                              data-size="xs"
                            >
                              {t('safetyRep.work.teams.persons', {
                                count: team.size,
                              })}
                            </span>
                          </span>
                        </Table.HeaderCell>
                        <Table.Cell>{teamLeaders(team, t)}</Table.Cell>
                        <Table.Cell>
                          <Tag data-size="sm" data-color={stageColor(team)}>
                            {stageText(team, t)}
                          </Tag>
                        </Table.Cell>
                        <Table.Cell>{highRiskText(team, t)}</Table.Cell>
                        <Table.Cell>
                          {lastSurveyText(team, t, i18n.language)}
                        </Table.Cell>
                        <Table.Cell>
                          {nextShort(team, data.tasks, t, i18n.language)}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </section>
        </div>
      ) : (
        <WorkState loading={isLoading} error={t('safetyRep.work.error')} />
      )}
    </div>
  );
};
